"use client";

import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useConfirmation } from "@/components/common/ConfirmationDialog";
import { TableContainer } from "@/components/common/TableContainer";
import { DataView } from "@/components/common/DataView";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Edit3, DollarSign, Percent, X, RefreshCw } from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import {
  StandardFormInput,
  StandardFormSelect,
} from "@/components/common/StandardFormInput";
import { ActionButton } from "@/components/common/DataViewTypes";
import { ProductSelector } from "@/components/common/ProductSelector";
import { RootState, AppDispatch } from "@/store/store";
import {
  fetchChargesRequest,
  createChargeRequest,
  updateChargeRequest,
  deleteChargeRequest,
  resetFormState,
} from "@/store/slices/charge.slice";
import { Charge, CreateChargeDto } from "@/types/models/charge";

interface AddChargeForm {
  name: string;
  description: string;
  amount: string;
  type: "percentage" | "fixed" | "";
  applicable_on_bill: boolean;
  applicable_on_products: boolean;
  applicable_on: string[];
}

const ChargesTab = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { confirm } = useConfirmation();

  const {
    items: charges,
    loaded,
    loading,
    error,
    pagination,
    formState,
  } = useSelector((state: RootState) => state.charges);
  const [pageIndex, setPageIndex] = useState(0);

  const fetchCharges = useCallback(() => {
    dispatch(fetchChargesRequest({ page: pageIndex + 1 }));
  }, [dispatch, pageIndex]);

  // Load data on component mount / pagination with guarded fetches
  useEffect(() => {
    if (loading) return;

    const hasNotFoundError = error?.status === 404;
    if (hasNotFoundError) return;

    const currentPageIndex = Math.max(0, (pagination?.page || 1) - 1);
    const hasPageMismatch = currentPageIndex !== pageIndex;

    if (!loaded && charges.length === 0) {
      fetchCharges();
      return;
    }

    if (loaded && hasPageMismatch) {
      fetchCharges();
    }
  }, [loaded, charges.length, loading, error?.status, pagination?.page, pageIndex, fetchCharges]);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingChargeId, setEditingChargeId] = useState<string | null>(null);
  const [addForm, setAddForm] = useState<AddChargeForm>({
    name: "",
    description: "",
    amount: "",
    type: "",
    applicable_on_bill: false,
    applicable_on_products: false,
    applicable_on: [],
  });

  // Reset form state when modal closes
  useEffect(() => {
    if (!isAddModalOpen) {
      dispatch(resetFormState());
      setIsEditMode(false);
      setEditingChargeId(null);
    }
  }, [isAddModalOpen, dispatch]);

  // Close modal and reset form when charge is successfully created
  useEffect(() => {
    if (formState.success && !formState.loading) {
      resetForm();
      setIsAddModalOpen(false);
    }
  }, [formState.success, formState.loading]);

  const chargeTypes = [
    {
      value: "percentage",
      label: "Percentage",
      icon: <Percent className="h-4 w-4" />,
      description: "Percentage-based charge calculation",
    },
    {
      value: "fixed",
      label: "Fixed Amount",
      icon: <DollarSign className="h-4 w-4" />,
      description: "Fixed amount charge",
    },
  ];

  const getTypeIcon = (type: Charge["type"]) => {
    const typeConfig = {
      percentage: {
        icon: <Percent className="h-4 w-4" />,
        color: "text-blue-600",
      },
      fixed: {
        icon: <DollarSign className="h-4 w-4" />,
        color: "text-green-600",
      },
    };

    return (
      <div className={`${typeConfig[type].color}`}>{typeConfig[type].icon}</div>
    );
  };

  const getTypeBadge = (type: Charge["type"]) => {
    const typeColors = {
      percentage: "bg-blue-500/20 text-blue-600 hover:bg-blue-500/20",
      fixed: "bg-green-500/20 text-green-600 hover:bg-green-500/20",
    };

    return (
      <Badge className={typeColors[type]}>
        {type === "percentage" ? "Percentage" : "Fixed"}
      </Badge>
    );
  };

  const handleAddCharge = () => {
    if (addForm.name && addForm.description && addForm.amount && addForm.type) {
      const chargeData: CreateChargeDto = {
        name: addForm.name,
        description: addForm.description,
        amount: parseFloat(addForm.amount),
        type: addForm.type as "percentage" | "fixed",
        applicable_on_bill: addForm.applicable_on_bill,
        applicable_on: addForm.applicable_on_products
          ? addForm.applicable_on
          : undefined,
      };

      if (isEditMode && editingChargeId) {
        dispatch(
          updateChargeRequest({
            id: editingChargeId,
            data: {
              ...chargeData,
              enabled: true,
              applicable_on_products: addForm.applicable_on_products,
              applicable_products: addForm.applicable_on_products
                ? addForm.applicable_on
                : undefined,
            },
          }),
        );
      } else {
        dispatch(createChargeRequest(chargeData));
      }
    }
  };

  const resetForm = () => {
    setAddForm({
      name: "",
      description: "",
      amount: "",
      type: "",
      applicable_on_bill: false,
      applicable_on_products: false,
      applicable_on: [],
    });
  };

  const handleEditCharge = (charge: Charge) => {
    setAddForm({
      name: charge.name,
      description: charge.description,
      amount: charge.amount.toString(),
      type: charge.type,
      applicable_on_bill: charge.applicable_on_bill,
      applicable_on_products: charge.applicable_on_products,
      applicable_on: charge.applicable_on || [],
    });
    setIsEditMode(true);
    setEditingChargeId(charge.id);
    setIsAddModalOpen(true);
  };

  const handleRemoveCharge = async (id: string) => {
    const result = await confirm({
      title: "Delete Charge",
      message:
        "Are you sure you want to delete this charge? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "destructive",
    });

    if (result.confirmed) {
      dispatch(deleteChargeRequest({ id }));
    }
  };

  const columns: ColumnDef<Charge>[] = [
    {
      accessorKey: "name",
      header: "Charge",
      size: 350,
      cell: ({ row }) => {
        const charge = row.original;
        return (
          <div className="flex items-center gap-3 min-w-[300px]">
            <div className="h-10 w-10 bg-muted/50 rounded-lg flex items-center justify-center">
              {getTypeIcon(charge.type)}
            </div>
            <div>
              <p className="font-medium text-foreground">{charge.name}</p>
              <div className="text-xs text-muted-foreground">
                {charge.description}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "type",
      header: "Type",
      cell: ({ row }) => {
        return getTypeBadge(row.original.type);
      },
    },
    {
      accessorKey: "amount",
      header: "Amount",
      cell: ({ row }) => {
        const charge = row.original;
        return (
          <div className="font-medium">
            {charge.type === "percentage"
              ? `${charge.amount}%`
              : `₹${charge.amount}`}
          </div>
        );
      },
    },
    {
      accessorKey: "application",
      header: "Application",
      cell: ({ row }) => {
        const charge = row.original;
        return (
          <div className="flex gap-2">
            {charge.applicable_on_bill && (
              <Badge className="text-xs bg-blue-500/20 text-blue-600 hover:bg-blue-500/20">
                Bill
              </Badge>
            )}
            {charge.applicable_on_products && (
              <Badge className="text-xs bg-green-500/20 text-green-600 hover:bg-green-500/20">
                Products
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "updated_at",
      header: "Last Updated",
      size: 120,
      cell: ({ row }) => {
        return (
          <div className="text-sm text-muted-foreground">
            {row.original.updated_at}
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      size: 120,
      cell: ({ row }) => {
        const charge = row.original;
        return (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => handleEditCharge(charge)}
            >
              <Edit3 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/20"
              onClick={() => handleRemoveCharge(charge.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  // Create action buttons
  const actionButtons: ActionButton[] = [
    {
      label: "Refresh",
      icon: <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />,
      onClick: fetchCharges,
      variant: "outline",
      disabled: loading,
    },
    {
      label: "Add Charge",
      icon: <Plus className="h-3 w-3" />,
      onClick: () => setIsAddModalOpen(true),
      variant: "primary",
    },
  ];

  return (
    <div>
      <TableContainer>
        <div className="bg-secondary-background rounded-lg p-4">
          <DataView
            title="Charges"
            description="Manage additional charges like shipping, handling, and service fees"
            columns={columns}
            data={charges}
            loading={loading}
            pagination={pagination}
            pageIndex={pageIndex}
            onPageChange={setPageIndex}
            emptyMessage="No charges found. Add your first charge to get started."
            actionButtons={actionButtons}
          />
        </div>
      </TableContainer>

      {/* Add Charge Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto bg-secondary-background">
          <DialogHeader>
            <DialogTitle>
              {isEditMode ? "Edit Charge" : "Add New Charge"}
            </DialogTitle>
            <DialogDescription>
              {isEditMode
                ? "Update the charge configuration."
                : "Create a charge that can be applied to orders and products."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-6 py-4">
            {/* Show form-level errors */}
            {formState.error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">
                  {formState.error.message}
                </p>
              </div>
            )}
            {/* Charge Name */}
            <StandardFormInput
              label="Charge Name"
              placeholder="e.g., Shipping Fee, Handling Fee, Service Charge"
              value={addForm.name}
              onChange={(e) =>
                setAddForm((prev) => ({ ...prev, name: e.target.value }))
              }
            />

            {/* Charge Description */}
            <StandardFormInput
              label="Description"
              placeholder="Enter charge description"
              value={addForm.description}
              onChange={(e) =>
                setAddForm((prev) => ({ ...prev, description: e.target.value }))
              }
            />

            {/* Charge Amount */}
            <StandardFormInput
              label="Charge Amount"
              type="number"
              step="0.01"
              placeholder="Enter amount"
              value={addForm.amount}
              onChange={(e) =>
                setAddForm((prev) => ({ ...prev, amount: e.target.value }))
              }
            />

            {/* Charge Type Selection */}
            <StandardFormSelect
              label="Charge Type"
              value={addForm.type}
              onValueChange={(value) =>
                setAddForm((prev) => ({
                  ...prev,
                  type: value as Charge["type"],
                }))
              }
              options={chargeTypes.map((type) => ({
                label: `${type.label} - ${type.description}`,
                value: type.value,
              }))}
              placeholder="Select charge calculation type"
            />

            {/* Toggle Options */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-4 bg-background rounded-lg">
                <div className="space-y-1">
                  <Label className="text-sm font-medium">Apply on Bill</Label>
                  <p className="text-xs text-muted-foreground">
                    {addForm.applicable_on_bill
                      ? "Currently applying to total bill"
                      : "Currently not applying to bill"}
                  </p>
                </div>
                <Switch
                  checked={addForm.applicable_on_bill}
                  onCheckedChange={(checked) =>
                    setAddForm((prev) => ({
                      ...prev,
                      applicable_on_bill: checked,
                      applicable_on_products: checked
                        ? false
                        : prev.applicable_on_products, // Mutually exclusive
                      applicable_on: checked ? [] : prev.applicable_on,
                    }))
                  }
                />
              </div>
              <div className="flex items-center justify-between p-4 bg-background rounded-lg">
                <div className="space-y-1">
                  <Label className="text-sm font-medium">
                    Apply on Products
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {addForm.applicable_on_products
                      ? "Currently applying to selected products"
                      : "Currently not applying to products"}
                  </p>
                </div>
                <Switch
                  checked={addForm.applicable_on_products}
                  onCheckedChange={(checked) =>
                    setAddForm((prev) => ({
                      ...prev,
                      applicable_on_products: checked,
                      applicable_on_bill: checked
                        ? false
                        : prev.applicable_on_bill, // Mutually exclusive
                      applicable_on: checked ? [] : prev.applicable_on,
                    }))
                  }
                />
              </div>
            </div>

            {/* Specific Products Selection */}
            {addForm.applicable_on_products && (
              <ProductSelector
                label="Specific Products"
                value={addForm.applicable_on}
                onValueChange={(value) =>
                  setAddForm((prev) => ({
                    ...prev,
                    applicable_on: value,
                  }))
                }
                placeholder="Select products to apply charge"
                multiSelect={true}
              />
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                resetForm();
                setIsAddModalOpen(false);
              }}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleAddCharge}
              disabled={
                !addForm.name ||
                !addForm.description ||
                !addForm.amount ||
                !addForm.type ||
                formState.loading
              }
            >
              <Plus className="h-4 w-4 mr-2" />
              {formState.loading
                ? "Saving..."
                : isEditMode
                  ? "Update Charge"
                  : "Add Charge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChargesTab;
